import React, { useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAIAnswer,
  askAI,
  submitAIFeedback,
  refineAIAnswer,
  publishAIAnswer,
  discardAIAnswer,
  clearAIError,
} from "../../store/aiAnswerSlice";
import {
  fetchAnswers,
  likeAnswer,
  markBestAnswer,
} from "../../store/queriesAnswerSlice";
import "./AskAI.css";

const AskAI = ({ questionId, aiEnabled, isQuestionOwner }) => {
  const dispatch = useDispatch();
  const {
    status,
    aiAnswer,
    loading,
    refining,
    publishing,
    discarding,
    feedbackLoading,
    error,
    publishedAnswer,
  } = useSelector((state) => state.aiAnswer);

  useEffect(() => {
    if (!questionId) {
      return;
    }

    dispatch(fetchAIAnswer(questionId));
  }, [dispatch, questionId]);

  const handleAskAI = () => {
    if (!questionId) {
      console.error("AskAI: questionId is missing");
      return;
    }
    dispatch(clearAIError());
    dispatch(askAI(questionId));
  };

  const handlePublish = async () => {
    if (!questionId) {
      return;
    }
    const feedbackResult = await dispatch(
      submitAIFeedback({
        questionId,
        helpful: true,
      }),
    );

    if (!submitAIFeedback.fulfilled.match(feedbackResult)) {
      return;
    }

    const publishResult = await dispatch(publishAIAnswer(questionId));
    if (publishAIAnswer.fulfilled.match(publishResult)) {
      dispatch(fetchAnswers(questionId));
    }
  };

  const handleDiscard = async () => {
    if (!questionId) {
      return;
    }

    const feedbackResult = await dispatch(
      submitAIFeedback({
        questionId,
        helpful: false,
      }),
    );

    if (!submitAIFeedback.fulfilled.match(feedbackResult)) {
      return;
    }
    dispatch(discardAIAnswer(questionId));
  };

  const handleRefine = (action) => {
    if (!questionId) {
      return;
    }

    dispatch(
      refineAIAnswer({
        questionId,
        action,
      }),
    );
  };

  const handleMarkBest = async (event) => {
    if (!event.target.checked || !publishedAnswer?.answerid) {
      return;
    }

    await dispatch(
      markBestAnswer({
        questionId,
        answerId: publishedAnswer.answerid,
      }),
    );

    dispatch(fetchAnswers(questionId));
  };

  const handleLike = () => {
    if (!publishedAnswer?.answerid) {
      return;
    }

    dispatch(
      likeAnswer({
        answer: publishedAnswer,
      }),
    );
  };

  if (loading && status === "none") {
    return (
      <Box className="ask-ai-wrapper">
        <Box className="ask-ai-box">
          <Box className="ask-ai-top-row">
            <Box className="ask-ai-generated-label">
              <img
                src={process.env.PUBLIC_URL + "/images/AskAI/gen_AI_icon.png"}
                alt="AI generated"
                className="gen-ai-icon"
              />

              <Typography sx={{ fontSize: "14px" }}>
                AI-generated answer
              </Typography>
            </Box>

            <Button className="ask-ai-main-button" disabled>
              Ask AI
            </Button>
          </Box>

          <Box className="ask-ai-divider" />

          <Box className="instant-ai-section">
            <Box className="instant-ai-title">
              <img
                src={
                  process.env.PUBLIC_URL + "/images/AskAI/instantAI_icon.png"
                }
                alt="Instant AI"
                className="instant-ai-icon"
              />

              <Typography className="instant_ai_typo">
                Get an instant AI answer
              </Typography>
            </Box>

            <Typography className="instant-ai-description">
              Click on <strong>Ask AI</strong> to generate an answer for this
              question. You can review it before publishing, refine it or
              request a human expert instead.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (status === "none" && aiEnabled && isQuestionOwner) {
    return (
      <Box className="ask-ai-wrapper">
        <Box className="ask-ai-box">
          <Box className="ask-ai-top-row">
            <Box className="ask-ai-generated-label">
              <img
                src={process.env.PUBLIC_URL + "/images/AskAI/gen_AI_icon.png"}
                alt="AI generated"
                className="gen-ai-icon"
              />

              <Typography sx={{ fontSize: "14px" }}>
                AI-generated answer
              </Typography>
            </Box>

            <Button
              className="ask-ai-main-button"
              onClick={handleAskAI}
              disabled={loading}
            >
              Ask AI
            </Button>
          </Box>

          <Box className="ask-ai-divider" />

          <Box className="instant-ai-section">
            <Box className="instant-ai-title">
              <img
                src={
                  process.env.PUBLIC_URL + "/images/AskAI/instantAI_icon.png"
                }
                alt="Instant AI"
                className="instant-ai-icon"
              />

              <Typography className="instant_ai_typo">
                Get an instant AI answer
              </Typography>
            </Box>

            <Typography className="instant-ai-description">
              Click on <strong>Ask AI</strong> to generate an answer for this
              question. You can review it before publishing, refine it or
              request a human expert instead.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (status === "draft") {
    if (!isQuestionOwner) {
      return null;
    }
    return (
      <Box className="ask-ai-box">
        <Box className="ask-ai-header">
          <Box className="ask-ai-label">
            <img
              src={process.env.PUBLIC_URL + "/images/AskAI/ai_gen_icon.png"}
              alt="AI generated"
              className="gen-ai-icon"
            />

            <Typography className="ask-ai-typo">AI-generated answer</Typography>
          </Box>

          <Typography className="ask-ai-review-status">
            Review before publishing
          </Typography>
        </Box>

        <Typography className="ask-ai-answer" sx={{ whiteSpace: "pre-wrap" }}>
          {aiAnswer}
        </Typography>

        <Typography className="ask-ai-refine-title">
          Refine this answer
        </Typography>

        <Box className="ask-ai-refine-options">
          <Button
            className="ask-ai-refine-button"
            disabled={refining}
            onClick={() => handleRefine("generate_again")}
          >
            Generate another
          </Button>

          <Button
            className="ask-ai-refine-button"
            disabled={refining}
            onClick={() => handleRefine("detailed")}
          >
            Show detailed version
          </Button>

          <Button
            className="ask-ai-refine-button"
            disabled={refining}
            onClick={() => handleRefine("code_example")}
          >
            Show code example
          </Button>

          <Button
            className="ask-ai-refine-button"
            disabled={refining}
            onClick={() => handleRefine("alternative")}
          >
            Show alternative approaches
          </Button>
        </Box>

        <Box className="ask-ai-helpful">
          <Box className="ask-ai-helpful-row">
            <Typography className="ask-ai-helpful-title">
              Is this answer helpful?
            </Typography>

            <Button
              className="ask-ai-publish-button"
              onClick={handlePublish}
              disabled={publishing || feedbackLoading || discarding}
            >
              <img
                src={
                  process.env.PUBLIC_URL + "/images/AskAI/thumps_up_icon.png"
                }
                alt="Publish"
                className="ask-ai-helpful-icon"
              />
              Yes, publish it
            </Button>

            <Button
              className="ask-ai-discard-button"
              onClick={handleDiscard}
              disabled={discarding || feedbackLoading || publishing}
            >
              <img
                src={
                  process.env.PUBLIC_URL + "/images/AskAI/thumps_down_icon.png"
                }
                alt="Discard"
                className="ask-ai-helpful-icon"
              />
              No, discard
            </Button>
          </Box>

          <Box className="ask-ai-auto-publish">
            <img
              src={process.env.PUBLIC_URL + "/images/AskAI/note_icon.png"}
              alt="Note"
              className="ask-ai-note-icon"
            />

            <Typography>
              If no human answer is received within 24 hours, the AI answer is
              automatically published.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {typeof error === "string" ? error : "Something went wrong."}
          </Typography>
        )}
      </Box>
    );
  }

  if (status === "published") {
    return (
      <Box className="ask-ai-box">
        <Box className="ask-ai-header">
          <Box className="ask-ai-label">
            <img
              src={process.env.PUBLIC_URL + "/images/AskAI/ai_gen_icon.png"}
              alt="AI generated"
              className="gen-ai-icon"
            />

            <Typography className="ask-ai-typo">AI-generated answer</Typography>
          </Box>

          <Box className="ask-ai-published-status">
            <img
              src={
                process.env.PUBLIC_URL + "/images/AskAI/AI_published_icon.png"
              }
              alt="AI answer published"
              className="gen-ai-icon"
            />

            <Typography>AI answer published successfully</Typography>
          </Box>
        </Box>

        <Typography className="ask-ai-answer" sx={{ whiteSpace: "pre-wrap" }}>
          {publishedAnswer?.answer || aiAnswer}
        </Typography>

        {/* <Box className="ask-ai-published-actions">
          <Box className="ask-ai-action-item">
            <input
              type="checkbox"
              className="ask-ai-circle-checkbox"
              // onChange={handleMarkBest}
              // disabled={!publishedAnswer?.answerid}
            />

            <span>Mark as Best Answer</span>
          </Box>

          <Box
            className="ask-ai-action-item"
            // onClick={handleLike}
            sx={{
              cursor: publishedAnswer?.answerid ? "pointer" : "default",
            }}
          >
            <img
              src={process.env.PUBLIC_URL + "/images/Home/like.png"}
              alt="Like"
              className="ask-ai-action-icon"
            />
          </Box>

          <Box className="ask-ai-action-item">
            <span className="ask-ai-icon-wrapper">
              <img
                src={process.env.PUBLIC_URL + "/images/Home/comment.png"}
                alt="Comment"
                className="ask-ai-action-icon ask-ai-comment-icon"
              />
            </span>
          </Box>
        </Box> */}
      </Box>
    );
  }

  return null;
};

export default AskAI;
